import { useState, useEffect } from "react";
import TestDragAndDrop from "../../../components/TestDragAndDrop";
import { Col, Row, Button, Modal, Label, ModalHeader, ModalBody, Input, ModalFooter, FormGroup } from "reactstrap";
import { formatCurrency } from "../../../utils/numberUtils";
import { useLockState } from "../../../components/userLocks/LockCheck";

const LineEditorTable = ({ itemRecurrence, groups, items, onGroupMoved, onItemMoved, onAddGroup, onEditGroup, onDeleteGroup, onGroupExpandedChanged, onUpdateVisible, onUpdateShowChildren }) => {

    const [openCreateGroup, setOpenCreateGroup] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
	const [selectedItem, setSelectedItem] = useState(null);
    const lockState = useLockState();

    const columns = [
        { key: "name", label: "Name", className: "fixed-col-150", render: (val, item) => {
            if (item.depth === 0) {
                return <strong>{val}</strong>;
            } else {
                return val;
            }
        }},
        { key: "quantity", label: "Qty", align: 'right' },

        ...((itemRecurrence.toLowerCase() === "upfront") ? [{ key: "totalUpfrontPrice", label: "Contract Upfront Price", className: "text-end", render: (val) => "£" + formatCurrency(val)}] : []),
        ...((itemRecurrence.toLowerCase() === "recurring") ? [{ key: "totalRecurringPrice", label: "Contract Recurring Price", className: "text-end", render: (val) => "£" + formatCurrency(val)}] : []),

        {
            key: "id",
            label: "Actions",
            isSortable: false,
            align: "right",
            render: (_, row) => {
                let hasChildren = false;
                let canHide = true;
                if (row.depth !== 0) {
                    if (row.isUpfront && row.totalUpfrontPrice !== 0) {
                        canHide = false;
                    } else if (row.isRecurring && row.totalRecurringPrice !== 0) {
                        canHide = false;
                    }
                } else {
                    const startIdx = items.findIndex(i => i.id === row.id);

                    if (startIdx >= 0) {
                        for (let i = startIdx + 1; i < items.length; i++) {
                            if (items[i].isUpfront && items[i].upfrontPrice !== 0) {
                                canHide = false;
                            } else if (items[i].isRecurring && items[i].recurringPrice !== 0) {
                                canHide = false;
                            }

                            if (items[i].depth <= row.depth) break;
                            if (items[i].depth === row.depth + 1) {
                                hasChildren = true;
                            } 
                        }
                    } 
                }

                if (row.depth === 0 && !hasChildren) {
                    return <div className={lockState?.isLocked ? "locked" : ""} onClick={(e) => { e.stopPropagation(); onDeleteGroup(row.id); }}><i className="bi bi-trash text-end text-danger" title="Delete Group"></i></div>
                } else if (row.depth === 1) {
                    if (row.visible && canHide) {
                        return <div className={lockState?.isLocked ? "locked" : ""} onClick={(e) => { e.stopPropagation(); onUpdateVisible(row.id, false); }}><i className="bi bi-eye text-end" title="Item is shown on quote"></i></div>
                    } else if (!row.visible) {
                        return <div className={lockState?.isLocked ? "locked" : ""} onClick={(e) => { e.stopPropagation(); onUpdateVisible(row.id, true); }}><i className="bi bi-eye-slash text-end" title="Item is hidden on quote"></i></div>
                    } else {
                        
                    }
                } else if (row.depth === 0) {
                    if (canHide) {
                        if (!row.visible) {
                            return <div className={lockState?.isLocked ? "locked" : ""} onClick={(e) => { e.stopPropagation(); onUpdateVisible(row.id, true); }}><i className="bi bi-eye-slash text-end" title="Group and its child items are not shown on quote"></i></div>
                        } else if (row.showChildren) {
                            return <div className={lockState?.isLocked ? "locked" : ""} onClick={(e) => {
                                e.stopPropagation();
                                onUpdateShowChildren(row.id, false);
                            }}><i className="bi bi-eye-fill text-end" title="Group and its child items are shown on the quote"></i></div>
                        } else {
                            return <div className={lockState?.isLocked ? "locked" : ""} onClick={(e) => {
                                e.stopPropagation();
                                onUpdateShowChildren(row.id, true);
                                onUpdateVisible(row.id, false);
                            }}><i className="bi bi-eye text-end" title="Group is shown on the quote, but the child items are not"></i></div>
                        } 
                    } else {
                        if (!row.showChildren) {
                            return <div className={lockState?.isLocked ? "locked" : ""} onClick={(e) => { e.stopPropagation(); onUpdateShowChildren(row.id, true); }}><i className="bi bi-eye text-end" title="Group is shown on the quote, but the child items are not"></i></div>
                        } else {
                            return <div className={lockState?.isLocked ? "locked" : ""} onClick={(e) => { e.stopPropagation(); onUpdateShowChildren(row.id, false); }}><i className="bi bi-eye-fill text-end" title="Group and its child items are shown on the quote"></i></div>
                        }
                    }

                }
            }
        }
    ];

    const closeCreateGroup = () => {
        setOpenCreateGroup(false);
 		setSelectedItem(null);
    }
    
    const addorEditGroup = () => {
        if (selectedItem) {
            onEditGroup?.(selectedItem.id, selectedItem.name);
            setSelectedItem(null);
        }
        else {
            onAddGroup?.(newGroupName)
            setNewGroupName('');
        }
        closeCreateGroup();
    }

    const onRowClick = (item) => {
        setSelectedItem(item);
        setOpenCreateGroup(true);
    }

    return (
        <>
            <Row>
                <Col style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Button className={lockState?.isLocked ? "locked" : ""} size="sm" onClick={() => setOpenCreateGroup((prev) => !prev)}>Create Group</Button>
                </Col>
            </Row>
            <Row className={lockState?.isLocked ? "locked" : ""}>
                <Col>
                    <TestDragAndDrop
                        items={items}
                        columns={columns}
                        onGroupMoved={onGroupMoved}
                        onItemMoved={onItemMoved}
                        onGroupExpandedChanged={onGroupExpandedChanged}
                        onRowClick={(row) => {
                            // Only allow editing for real items (groups)
                            if (!row || row.rowType === "child") return;
                            onRowClick(row);
                        }}
                    />

                </Col>
            </Row>
            <Modal isOpen={openCreateGroup} toggle={closeCreateGroup} centered size="xl" scrollable backdrop="static">
                <ModalHeader toggle={closeCreateGroup}>{selectedItem ? "Edit Group" : "Add New Group"}</ModalHeader>
                <ModalBody>
                    <FormGroup row>
                        <Label sm={3}>Name</Label>
                        <Col sm={9}>
                            <Input
                                value={selectedItem ? selectedItem.name : newGroupName}
                                onChange={(e) => selectedItem ? setSelectedItem({ ...selectedItem, name: e.target.value }) : setNewGroupName(e.target.value)}
                            />
                        </Col>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={closeCreateGroup}>
                        Cancel
                    </Button>
                    <Button color="secondary" onClick={addorEditGroup}>
                        Submit
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}
export default LineEditorTable;